import type {
    GetCommentTagsResponse,
    GetScoresByAttributesRequestQuery,
    GetScoresByCategoryResponse,
    GetScoresByCategoryXAttributesQuery,
    GetScoresByQuestionResponse,
    GetScoresResponse,
    GetSurveyAttributesResponse,
    GetSurveyCommentQuestionsResponse,
    GetSurveyCommentsRequestQuery,
    GetSurveyCommentsResponse,
    GetSurveyDataRequestParams,
    GetSurveyPermissionsResponse,
    GetSurveyScoresByCategoryXAttributesResponse,
    GetSurveyScoresByQuestionXAttributesResponse,
    GetSurveysDataRequestQuery,
    GetSurveysResponse,
    GetSurveysScoresByAttributesResponse,
    GetUserSurveyPermissionsResponse,
    SaveSurveyUserPermissionRequestBody,
    SaveSurveyUserPermissionResponse,
} from '@engagement-web-app/common';
import { APIError } from '../types.js';

export async function fetchSurveys(): Promise<GetSurveysResponse> {
    try {
        const res = await fetch(`/api/surveys`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyPermissions(): Promise<GetSurveyPermissionsResponse> {
    try {
        const res = await fetch(`/api/surveys/permissions`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchScores({ surveyIds }: GetSurveysDataRequestQuery): Promise<GetScoresResponse> {
    try {
        const params = new URLSearchParams();
        params.set('surveyIds', JSON.stringify(surveyIds));
        const res = await fetch(`/api/surveys/scores?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchScoresByCategory({
    surveyIds,
}: GetSurveysDataRequestQuery): Promise<GetScoresByCategoryResponse> {
    try {
        const params = new URLSearchParams();
        params.set('surveyIds', JSON.stringify(surveyIds));
        const res = await fetch(`/api/surveys/scores/categories?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchScoresByQuestion({
    surveyIds,
}: GetSurveysDataRequestQuery): Promise<GetScoresByQuestionResponse> {
    try {
        const params = new URLSearchParams();
        params.set('surveyIds', JSON.stringify(surveyIds));
        const res = await fetch(`/api/surveys/scores/questions?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyAttributes({
    surveyId,
}: GetSurveyDataRequestParams): Promise<GetSurveyAttributesResponse> {
    try {
        const res = await fetch(`/api/surveys/${surveyId}/attributes`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyScoresByAttributes({
    surveyIds,
    settingId,
}: GetScoresByAttributesRequestQuery): Promise<GetSurveysScoresByAttributesResponse> {
    try {
        const params = new URLSearchParams();
        params.set('surveyIds', JSON.stringify(surveyIds));
        params.set('settingId', settingId);
        const res = await fetch(`/api/surveys/scores/attributes?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyScoresByCategoryXAttributes(
    { surveyId }: GetSurveyDataRequestParams,
    { attributes }: GetScoresByCategoryXAttributesQuery
): Promise<GetSurveyScoresByCategoryXAttributesResponse> {
    try {
        const params = new URLSearchParams();
        params.set('attributes', JSON.stringify(attributes));
        const res = await fetch(`/api/surveys/${surveyId}/scores/categories?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyScoresByQuestionXAttributes(
    { surveyId }: GetSurveyDataRequestParams,
    { attributes }: GetScoresByCategoryXAttributesQuery
): Promise<GetSurveyScoresByQuestionXAttributesResponse> {
    try {
        const params = new URLSearchParams();
        params.set('attributes', JSON.stringify(attributes));
        const res = await fetch(`/api/surveys/${surveyId}/scores/questions?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyCommentQuestions({
    surveyId,
}: GetSurveyDataRequestParams): Promise<GetSurveyCommentQuestionsResponse> {
    try {
        const res = await fetch(`/api/surveys/${surveyId}/questions/comments`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyComments(
    { surveyId }: GetSurveyDataRequestParams,
    { questionIds, sentiment, tagIds, search, page, pageSize }: GetSurveyCommentsRequestQuery
): Promise<GetSurveyCommentsResponse> {
    try {
        const params = new URLSearchParams();
        if (questionIds && questionIds.length > 0) params.set('questionIds', JSON.stringify(questionIds));
        if (sentiment) params.set('sentiment', sentiment);
        if (tagIds && tagIds.length > 0) params.set('tagIds', JSON.stringify(tagIds));
        if (search) params.set('search', search);
        if (page !== undefined) params.set('page', String(page));
        if (pageSize !== undefined) params.set('pageSize', String(pageSize));
        const qs = params.toString();
        const res = await fetch(`/api/surveys/${surveyId}/comments${qs ? `?${qs}` : ''}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchSurveyCommentTags({
    surveyId,
}: GetSurveyDataRequestParams): Promise<GetCommentTagsResponse> {
    try {
        const res = await fetch(`/api/surveys/${surveyId}/comments/tags`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchUserSurveyPermissions(userId: string): Promise<GetUserSurveyPermissionsResponse> {
    try {
        const res = await fetch(`/api/users/${userId}/survey-permissions`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function saveUserSurveyPermission(
    userId: string,
    surveyId: string,
    body: SaveSurveyUserPermissionRequestBody
): Promise<SaveSurveyUserPermissionResponse> {
    try {
        const res = await fetch(`/api/users/${userId}/survey-permissions/${surveyId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function revokeUserSurveyPermission(userId: string, surveyId: string): Promise<void> {
    try {
        const res = await fetch(`/api/users/${userId}/survey-permissions/${surveyId}`, { method: 'DELETE' });
        if (!res.ok) throw APIError.fromResponse(await res.json());
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}
